import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import Alert from "@redotech/redo-icons/alert-triangle.svg";
import Info from "@redotech/redo-icons/info-circle.svg";
import Note from "@redotech/redo-icons/sticker-square.svg";
import { Step as ModelStep } from "@redotech/redo-return-core-common/return-flow";
import {
  AiReturnReasonsState,
  isAiReasonsStepTitle,
} from "@redotech/redo-return-ui/flows/return-flow/ai-return-reasons";
import {
  MULTIPLE_CHOICE,
  MultipleChoiceState,
} from "@redotech/redo-return-ui/flows/return-flow/multiple-choice";
import { Step, StepId, TypedStep } from "@redotech/redo-return-ui/flows/step";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Card, CollapseSubsection } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { unique } from "@redotech/util/array";
import { memo, SetStateAction, useMemo } from "react";
import { PRODUCTS_AS_VARIANTS_PATH } from "../returns/portal/portal-links";
import { AdditionalAiInfo, MultipleChoiceDetails } from "./multiple-choice";
import { getAllPathsToStep, RETURN_REASON_TITLE } from "./util";

export const ExchangeAgentConfiguration = memo(
  function ExchangeAgentConfiguration({
    allSteps,
    step,
    canEditSettings,
    setStateWithId,
  }: {
    allSteps: Map<StepId, Step>;
    step: Step;
    canEditSettings: boolean;
    setStateWithId: (
      id: StepId,
      passedStep: SetStateAction<Step | undefined>,
      state: any,
    ) => void;
  }) {
    const { t } = useTranslation();
    const { upstreamMultipleChoiceSteps, upstreamAiReturnReasonsSteps } =
      useMemo(() => {
        const upstreamSteps = getUpstreamSteps(allSteps, step);

        const multipleChoiceStepType = MULTIPLE_CHOICE(MultipleChoiceDetails);
        const multipleChoiceSteps = upstreamSteps
          .map((id) => {
            const step = allSteps.get(id);
            if (
              step?.type.title === multipleChoiceStepType.title ||
              step?.type.title === RETURN_REASON_TITLE
            ) {
              return { id, step };
            }
            return undefined;
          })
          .filter(
            (
              stepWithId,
            ): stepWithId is {
              id: StepId;
              step: TypedStep<MultipleChoiceState, ModelStep.MultipleChoice>;
            } => !!stepWithId,
          );

        const aiReturnReasonsSteps = upstreamSteps
          .map((id) => {
            const step = allSteps.get(id);
            if (step && isAiReasonsStepTitle(step.type.title)) {
              return { id, step };
            }
            return undefined;
          })
          .filter(
            (
              stepWithId,
            ): stepWithId is {
              id: StepId;
              step: TypedStep<AiReturnReasonsState, ModelStep.AiReturnReasons>;
            } => !!stepWithId,
          );

        return {
          upstreamMultipleChoiceSteps: multipleChoiceSteps,
          upstreamAiReturnReasonsSteps: aiReturnReasonsSteps,
        };
      }, [allSteps, step]);

    const handleAiReturnReasonsAdditionalAiInfo = useHandler(
      (id: StepId, passedStep: Step, additionalAiInfo: string | undefined) =>
        setStateWithId(id, passedStep, (state: AiReturnReasonsState) => ({
          ...state,
          additionalAiInfo,
        })),
    );

    const handleStepAdditionalAiInfo = useHandler(
      (id: StepId, passedStep: Step, additionalAiInfo: string | undefined) =>
        setStateWithId(id, passedStep, (state: MultipleChoiceState) => ({
          ...state,
          additionalAiInfo,
        })),
    );

    const handleChoiceAdditionalAiInfo = useHandler(
      (
        id: StepId,
        passedStep: Step,
        index: number,
        additionalAiInfo: string | undefined,
      ) =>
        setStateWithId(id, passedStep, (state: MultipleChoiceState) => ({
          ...state,
          choices: state.choices.map((choice, i) =>
            i === index ? { ...choice, additionalAiInfo } : choice,
          ),
        })),
    );

    return (
      <Flex flexDirection="column" gap="xl">
        <Flex flexDirection="column" gap="xl">
          <Flex w="half">
            <RedoExternalLink
              fontSize="md"
              text={t("Exchange Settings")}
              url={getConfigureProductsAsVariantsUrl()}
            />
          </Flex>
        </Flex>
        <Flex flexDirection="column" gap="xl">
          <Card>
            <CollapseSubsection
              title={
                <Flex gap="lg">
                  <Text>{t("Flow path Variant Exchange Guidance")}</Text>
                  <Tooltip
                    enterTouchDelay={0}
                    title={t(
                      "Add additional guidance to help Exchange Agent make better variant exchange recommendations. Gives Exchange Agent the guidance attached to each step that the customer goes down.",
                    )}
                  >
                    <Flex>
                      <Info />
                    </Flex>
                  </Tooltip>
                  <Tooltip
                    enterTouchDelay={0}
                    title={t(
                      "IMPORTANT: Put product specific guidance in Exchange Settings, not here.",
                    )}
                  >
                    <Flex color="orange">
                      <Alert />
                    </Flex>
                  </Tooltip>
                </Flex>
              }
            >
              <Flex flexDirection="column" gap="xl">
                {upstreamAiReturnReasonsSteps.map((stepWithId, index) => (
                  <Flex flexDirection="column" gap="xl" key={index}>
                    <Text fontWeight="bold">{t("AI Return Reasons")}</Text>
                    <AdditionalAiInfo
                      canEditReturnsSettings={canEditSettings}
                      collapsedTitle={t("Conversation level guidance")}
                      currentAdditionalAiInfo={
                        stepWithId.step.state.additionalAiInfo
                      }
                      setAdditionalAiInfo={(additionalAiInfo) =>
                        handleAiReturnReasonsAdditionalAiInfo(
                          stepWithId.id,
                          stepWithId.step,
                          additionalAiInfo,
                        )
                      }
                      tooltip={
                        <VariantExchangeContextTitleTooltip
                          hasAdditionalAiInfo={
                            !!stepWithId.step.state.additionalAiInfo
                          }
                        />
                      }
                    />
                  </Flex>
                ))}
                {upstreamMultipleChoiceSteps.map((stepWithId, index) => (
                  <Flex flexDirection="column" gap="xl" key={index}>
                    <Text fontWeight="bold">
                      {stepWithId.step.state.prompt}
                    </Text>
                    <Flex flexDirection="column" gap="3xl">
                      <AdditionalAiInfo
                        canEditReturnsSettings={canEditSettings}
                        collapsedTitle={t("Step level guidance")}
                        currentAdditionalAiInfo={
                          stepWithId.step.state.additionalAiInfo
                        }
                        setAdditionalAiInfo={(additionalAiInfo) =>
                          handleStepAdditionalAiInfo(
                            stepWithId.id,
                            stepWithId.step,
                            additionalAiInfo,
                          )
                        }
                        tooltip={
                          <VariantExchangeContextTitleTooltip
                            hasAdditionalAiInfo={
                              !!stepWithId.step.state.additionalAiInfo
                            }
                          />
                        }
                      />
                      <RedoCollapseSubsection
                        title={t("Choices")}
                        tooltip={
                          <VariantExchangeContextTitleTooltip
                            hasAdditionalAiInfo={stepWithId.step.state.choices.some(
                              (choice) => choice.additionalAiInfo,
                            )}
                          />
                        }
                      >
                        <Flex flexDirection="column" gap="xl">
                          {stepWithId.step.state.choices.map(
                            (choice, index) => (
                              <Flex flexDirection="column" gap="xl" key={index}>
                                <Divider />
                                <Text>{choice.name}</Text>
                                <AdditionalAiInfo
                                  canEditReturnsSettings={canEditSettings}
                                  collapsedTitle={t("Choice level guidance")}
                                  currentAdditionalAiInfo={
                                    choice.additionalAiInfo
                                  }
                                  setAdditionalAiInfo={(additionalAiInfo) =>
                                    handleChoiceAdditionalAiInfo(
                                      stepWithId.id,
                                      stepWithId.step,
                                      index,
                                      additionalAiInfo,
                                    )
                                  }
                                  tooltip={
                                    <VariantExchangeContextTitleTooltip
                                      hasAdditionalAiInfo={
                                        !!choice.additionalAiInfo
                                      }
                                    />
                                  }
                                />
                              </Flex>
                            ),
                          )}
                        </Flex>
                      </RedoCollapseSubsection>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            </CollapseSubsection>
          </Card>
        </Flex>
      </Flex>
    );
  },
);

function getConfigureProductsAsVariantsUrl() {
  const baseUrl = window.location.href.split("/settings")[0];

  return `${baseUrl}/settings/${PRODUCTS_AS_VARIANTS_PATH}`;
}

const VariantExchangeContextTitleTooltip = memo(
  function VariantExchangeContextTitleTooltip({
    hasAdditionalAiInfo,
  }: {
    hasAdditionalAiInfo?: boolean;
  }) {
    const { t } = useTranslation();
    return (
      <Flex gap="lg">
        {hasAdditionalAiInfo && (
          <Tooltip
            enterTouchDelay={0}
            title={t("Section contains additional context")}
          >
            <Flex>
              <Note />
            </Flex>
          </Tooltip>
        )}
      </Flex>
    );
  },
);

function getUpstreamSteps(allSteps: Map<StepId, Step>, step: Step) {
  const startStep = Array.from(allSteps.values()).find((step) => !!step.start);

  const currentStepId = Array.from(allSteps.entries()).find(
    ([id, s]) => s === step,
  )?.[0];
  const startStepId = Array.from(allSteps.entries()).find(
    ([id, s]) => s === startStep,
  )?.[0];
  if (!startStepId || !currentStepId) {
    return [];
  }

  return unique(getAllPathsToStep(startStepId, currentStepId, allSteps).flat());
}
